import { CATALOG_SORTS, formatAmount, parseAmount } from "@loot-os/api-contract";
import type {
  AmountField,
  BoxFiltersContent,
} from "@loot-os/design-system/templates/boxes/boxesContent";
import { useState } from "react";
import { useCurrency } from "../../../../lib/currency.js";
import { useShopper } from "../../../../lib/shopper.js";
import { useCatalogCopy } from "./copy.js";
import { useBoxesSearch } from "./search.js";

/**
 * A field over a URL value: it shows what the shopper typed and writes what that parses to, and
 * starts over when the URL moves on its own (Clear, back and forward) rather than echoing an edit.
 */
function useUrlDraft<T>(
  committed: T | undefined,
  format: (value: T) => string,
  parse: (text: string) => T | undefined,
  commit: (value: T | undefined) => void,
) {
  const toText = (value: T | undefined) => (value === undefined ? "" : format(value));
  const [draft, setDraft] = useState(() => ({ text: toText(committed), committed }));
  let text = draft.text;
  if (draft.committed !== committed) {
    text = parse(draft.text) === committed ? draft.text : toText(committed);
    setDraft({ text, committed });
  }
  return {
    text,
    onChange: (next: string) => {
      setDraft((current) => ({ ...current, text: next }));
      commit(parse(next));
    },
  };
}

/** The search, price, sort and affordability filters, all of which live in the URL. */
export function useBoxFiltersContent(): BoxFiltersContent {
  const copy = useCatalogCopy().filters;
  const currency = useCurrency();
  const { user } = useShopper();
  const [search, update] = useBoxesSearch();

  const parseText = (text: string) => (text === "" ? undefined : text);
  const parseCents = (text: string) => {
    if (text.trim() === "") return undefined;
    const cents = parseAmount(text, currency);
    return cents === null || cents < 0 ? undefined : cents;
  };
  // Bare, like what is typed: the field's addon carries the symbol.
  const formatCents = (cents: number) =>
    formatAmount(cents, { symbol: "", multiplier: currency.multiplier });

  const query = useUrlDraft(
    search.q,
    (q) => q,
    parseText,
    (q) => update({ q }),
  );
  const min = useUrlDraft(search.min, formatCents, parseCents, (cents) => update({ min: cents }));
  const max = useUrlDraft(search.max, formatCents, parseCents, (cents) => update({ max: cents }));
  const amountField = (
    field: { text: string; onChange: (text: string) => void },
    words: { label: string; placeholder: string },
  ): AmountField => ({
    value: field.text,
    label: words.label,
    placeholder: words.placeholder,
    onChange: field.onChange,
    invalid: field.text.trim() !== "" && parseCents(field.text) === undefined,
  });

  const activeCount = [
    search.q?.trim(),
    search.min !== undefined,
    search.max !== undefined,
    search.sort !== undefined,
    search.enough,
  ].filter(Boolean).length;

  return {
    search: {
      value: query.text,
      label: copy.search.label,
      placeholder: copy.search.placeholder,
      onChange: query.onChange,
    },
    price: {
      currency,
      min: amountField(min, copy.priceMin),
      max: amountField(max, copy.priceMax),
    },
    sort: {
      value: search.sort ?? "default",
      label: copy.sort.label,
      options: CATALOG_SORTS.map((value) => ({ value, label: copy.sort.options[value] })),
      onValueChange: (sort) => update({ sort: sort === "default" ? undefined : sort }),
    },
    affordable: user
      ? {
          checked: search.enough === true,
          label: copy.affordable,
          onCheckedChange: (checked) => update({ enough: checked ? true : undefined }),
        }
      : null,
    activeCount,
    clear: {
      label: copy.clear,
      onClear: () =>
        update({
          q: undefined,
          min: undefined,
          max: undefined,
          sort: undefined,
          enough: undefined,
        }),
    },
    sheet: {
      label: copy.sheet.label,
      countLabel: copy.sheet.countLabel,
      title: copy.sheet.title,
      doneLabel: copy.sheet.done,
    },
  };
}
